'use client'

import type { Data } from '@repo/strapi'
import AppLink from '@/components/elementary/AppLink'
import { KnockAi } from '@/components/elementary/KnockAi'
import { NavbarMenuLink } from '@/components/elementary/navbar/NavbarMenuLink'
import { Paragraph } from '@/components/typography/Paragraph'
import { Button } from '@/components/ui/button'
import {
	NavigationMenuContent,
	NavigationMenuItem,
	NavigationMenuTrigger,
} from '@/components/ui/navigation-menu'
import { cn } from '@/lib/styles'
import { NAVBAR_TEST_IDS } from '@/lib/test-ids'

interface CTAButtonsProps {
	logInLink?: Data.Component<'utilities.link'> | null
	primaryButtons?: Data.Component<'utilities.link'>[] | null
	dropdownItem?: Data.Component<'layout-utilities.dropdown-item'> | null
	knockAiId?: string | null
	minimal?: boolean
}

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: No cognitive complexity
export function CTAButtons({
	logInLink,
	primaryButtons,
	dropdownItem,
	knockAiId,
	minimal,
}: CTAButtonsProps) {
	return (
		<div className="flex items-center justify-end w-full">
			{logInLink?.href && logInLink?.label ? (
				<div className={cn('flex items-center lg:w-fit w-full min-h-8', minimal && 'mr-2')}>
					<AppLink
						href={logInLink.href ?? '#'}
						size={minimal ? 'small' : null}
						variant={minimal ? 'secondary' : 'primaryLink'}
						data-test-id={NAVBAR_TEST_IDS.LOGIN_BUTTON}
					>
						{minimal ? (
							logInLink.label
						) : (
							<Paragraph fontSize="bodyRegularMedium" className="text-inherit">
								{logInLink.label}
							</Paragraph>
						)}
					</AppLink>
					{!minimal && <div className="mx-4 w-px bg-neutral-light-500 h-6" />}
				</div>
			) : null}

			<div className="flex lg:flex-row flex-col items-center gap-2 lg:w-fit w-full">
				{primaryButtons && primaryButtons.length > 0
					? primaryButtons.map((primaryButton) =>
							primaryButton.href ? (
								<AppLink
									key={primaryButton.id}
									size="small"
									variant="primary"
									href={primaryButton.href}
									data-test-id={NAVBAR_TEST_IDS.PRIMARY_BUTTON}
								>
									{primaryButton.label}
								</AppLink>
							) : null,
						)
					: null}

				{dropdownItem?.label && dropdownItem.links && dropdownItem.links?.length > 0 ? (
					<NavigationMenuItem>
						<Button variant="secondary" size="small" asChild>
							<NavigationMenuTrigger className="[&>svg]:hidden">
								{dropdownItem.label}
							</NavigationMenuTrigger>
						</Button>
						<NavigationMenuContent
							className="min-w-61.5 p-4 border-none shadow-box divide-y divide-neutral-light-500 left-[unset] right-0"
							asChild
						>
							<ul>
								{dropdownItem?.links?.map((oneLink) => (
									<li key={oneLink.id} className="block py-2">
										<NavbarMenuLink
											link={oneLink}
											isDropDownItem
											data-test-id={NAVBAR_TEST_IDS.DROPDOWN_ITEM}
										/>
									</li>
								))}
							</ul>
						</NavigationMenuContent>
					</NavigationMenuItem>
				) : null}
			</div>
			{knockAiId ? (
				<div className="ml-2">
					<KnockAi dataId={knockAiId} />
				</div>
			) : null}
		</div>
	)
}
